import React, { useState, useEffect, useRef } from 'react';
import { X, Send } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';
import { PLATFORM_WA, openWhatsApp } from '../constants';

const fns = getFunctions(app);
const callChatWithAI = httpsCallable(fns, 'chatWithAI');

const INITIAL_QUICK_ES = ['¿Cuánto cuesta?', '¿Cómo funciona?', '¿Cómo pago?', '¿Tienen garantía?', 'Zonas de cobertura'];
const INITIAL_QUICK_ZH = ['多少钱', '如何使用', '如何支付', '有质保吗', '服务区域'];

function formatAnswer(text) {
  return text.split('\n').map((line, i, arr) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <span key={i}>
        {parts.map((part, j) => j % 2 === 1 ? <b key={j}>{part}</b> : part)}
        {i < arr.length - 1 && <br />}
      </span>
    );
  });
}

export default function FIXIChat({ lang, forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const getInitialMsg = (l) => ({
    id: 0, type: 'ai', greeting: true,
    followUps: l === 'zh' ? INITIAL_QUICK_ZH : INITIAL_QUICK_ES
  });

  useEffect(() => {
    setMessages([getInitialMsg(lang)]);
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  useEffect(() => {
    if (open) {
      setUnread(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      if (onClose) onClose();
    }
  }, [open, messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setUnread(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    setInput('');

    // WhatsApp escalation shortcut
    if (text === 'Hablar con soporte' || text === '联系客服') {
      const userMsg = { id: Date.now(), type: 'user', text };
      const aiMsg = {
        id: Date.now() + 1, type: 'ai',
        text: lang === 'es'
          ? '¡Claro! Te conecto con soporte en WhatsApp. Respondemos en < 5 minutos. 😊'
          : '好的！正在为您转接WhatsApp人工客服，5分钟内回复。😊',
        followUps: [],
        ctaWhatsApp: true
      };
      setMessages(prev => [...prev, userMsg, aiMsg]);
      setTimeout(() => {
        openWhatsApp(PLATFORM_WA, lang === 'es' ? '¡Hola! Necesito ayuda con FixBox.' : '你好！我需要FixBox客服帮助。');
      }, 1000);
      return;
    }

    const userMsg = { id: Date.now(), type: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setTyping(true);

    try {
      // Build message history for Claude (last 8 turns, excluding greeting)
      const apiHistory = messages
        .filter(m => !m.greeting && m.text)
        .slice(-7)
        .map(m => ({ role: m.type === 'user' ? 'user' : 'assistant', content: m.text }));
      apiHistory.push({ role: 'user', content: text });

      const { data } = await callChatWithAI({ messages: apiHistory, lang });

      setTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        text: data.reply,
        followUps: []
      }]);
    } catch (err) {
      console.error('FIXI AI error:', err);
      setTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        text: lang === 'es'
          ? 'Lo siento, hubo un error. Escríbenos por WhatsApp: +50766131328'
          : '抱歉，出现了错误，请通过WhatsApp联系我们：+50766131328',
        followUps: [],
        ctaWhatsApp: true
      }]);
    }
  };

  return (
    <>
      {/* 浮动按钮 */}
      <div
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', bottom: '74px', right: '16px',
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
          boxShadow: '0 6px 20px rgba(37,99,235,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', zIndex: 900,
          border: '2.5px solid #fff', transition: 'transform 0.2s'
        }}>
        <span style={{ fontSize: '22px', lineHeight: 1 }}>🤖</span>
        {unread && !open && (
          <div style={{
            position: 'absolute', top: '-2px', right: '-2px',
            width: '14px', height: '14px', borderRadius: '50%',
            background: '#EF4444', border: '2px solid #fff',
            animation: 'pulse 1.5s infinite'
          }} />
        )}
      </div>

      {/* 聊天模态 */}
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.48)', zIndex: 1000, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div style={{ background: '#F8FAFC', borderRadius: '20px 20px 0 0', height: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 -8px 40px rgba(0,0,0,0.20)' }}>

            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #0F1F35 0%, #1D4ED8 100%)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🤖</div>
              <div style={{ flex: 1 }}>
                <b style={{ color: '#fff', fontSize: '15px', display: 'block' }}>FIXI</b>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ADE80' }} />
                  <span style={{ color: '#94A3B8', fontSize: '11px', fontWeight: '700' }}>
                    {lang === 'es' ? 'Asistente FixBox · En línea' : 'FixBox AI 助手 · 在线'}
                  </span>
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}>
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {messages.map(msg => (
                <div key={msg.id}>
                  {msg.type === 'ai' && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>🤖</div>
                      <div style={{ maxWidth: '82%' }}>
                        {msg.greeting ? (
                          <div style={{ background: '#fff', borderRadius: '4px 14px 14px 14px', padding: '12px 14px', border: '1px solid #E3E8EF', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <p style={{ margin: '0 0 4px 0', fontSize: '15px' }}>
                              {lang === 'es' ? '¡Hola! Soy FIXI 👋' : '你好！我是 FIXI 👋'}
                            </p>
                            <p style={{ margin: 0, fontSize: '13px', color: '#475569', fontWeight: '600', lineHeight: '1.4' }}>
                              {lang === 'es' ? '¿En qué te puedo ayudar hoy?' : '今天有什么可以帮助您的？'}
                            </p>
                          </div>
                        ) : (
                          <div style={{ background: '#fff', borderRadius: '4px 14px 14px 14px', padding: '11px 13px', border: '1px solid #E3E8EF', fontSize: '13px', color: '#1E293B', lineHeight: '1.6', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            {formatAnswer(msg.text)}
                          </div>
                        )}

                        {msg.followUps?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                            {msg.followUps.map(q => (
                              <button key={q} onClick={() => sendMessage(q)}
                                style={{ padding: '6px 11px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '20px', fontSize: '11px', color: '#1D4ED8', fontWeight: '800', cursor: 'pointer' }}>
                                {q}
                              </button>
                            ))}
                          </div>
                        )}

                        {msg.ctaWhatsApp && (
                          <button
                            onClick={() => openWhatsApp(PLATFORM_WA, lang === 'es' ? '¡Hola! Necesito ayuda con FixBox.' : '你好！我需要FixBox客服帮助。')}
                            style={{ marginTop: '8px', padding: '10px 16px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            💬 {lang === 'es' ? 'Abrir WhatsApp' : '打开 WhatsApp'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {msg.type === 'user' && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{ maxWidth: '75%', background: 'linear-gradient(135deg, #2563EB, #1D4ED8)', color: '#fff', borderRadius: '14px 4px 14px 14px', padding: '10px 13px', fontSize: '13px', fontWeight: '600', lineHeight: '1.4' }}>
                        {msg.text}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {typing && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>🤖</div>
                  <div style={{ background: '#fff', border: '1px solid #E3E8EF', borderRadius: '4px 14px 14px 14px', padding: '12px 16px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#94A3B8', animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '10px 12px', background: '#fff', borderTop: '1px solid #E3E8EF', display: 'flex', gap: '8px', flexShrink: 0 }}>
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                placeholder={lang === 'es' ? 'Escribe tu pregunta...' : '输入您的问题...'}
                style={{ flex: 1, padding: '11px 14px', borderRadius: '12px', border: '1px solid #D8DEE8', background: '#F8FAFC', fontSize: '14px', color: '#1E293B', outline: 'none', fontFamily: 'inherit', fontWeight: '600' }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || typing}
                style={{ width: '44px', height: '44px', borderRadius: '12px', background: (input.trim() && !typing) ? '#2563EB' : '#E2E8F0', border: 'none', cursor: (input.trim() && !typing) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s', flexShrink: 0 }}>
                <Send size={18} color={(input.trim() && !typing) ? '#fff' : '#94A3B8'} />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
      `}</style>
    </>
  );
}
